import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AlertTriangleIcon, ArrowRightLeftIcon, CheckIcon, EyeIcon, FileTextIcon, FlagIcon, KeyRoundIcon, ShieldIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Input, Textarea } from '../ui/Field';
import { Modal } from '../ui/Modal';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import type { CampaGift } from '../../types/campagift';
import { STATUS_LABEL, TRANSFER_LABEL, bpsLabel, koboToNaira, statusTone } from '../../utils/campagift';
const FUNDING_LABEL: Record<string, string> = {
  user_purchase: 'Purchased value — funded by a user payment',
  merchant_promotion: 'Promotional value — funded by the merchant',
  campuzon_promotion: 'Promotional value — funded by Campuzon',
  campaign_budget: 'Campaign value — funded from a campaign budget',
  reward: 'Reward value'
};
export function GiftDetail({
  gift
}: {
  gift: CampaGift;
}) {
  const {
    typeOf,
    remaining,
    status,
    redemptions,
    accept,
    decline,
    transfer,
    revealCode,
    revealedCodes,
    raiseDispute,
    merchants
  } = useCampaGift();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferError, setTransferError] = useState<string | null>(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeKind, setDisputeKind] = useState('Incorrect redemption amount');
  const [disputeDetail, setDisputeDetail] = useState('');
  const [disputeSent, setDisputeSent] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const type = typeOf(gift);
  const value = remaining(gift);
  const state = status(gift);
  const code = revealedCodes[gift.id];
  const redeemable = state === 'available' || state === 'partially_redeemed';
  const history = redemptions.filter(r => r.giftId === gift.id).sort((a, b) => a.at < b.at ? 1 : -1);
  const eligibleMerchants = merchants.filter(m => m.campaGiftEnabled && m.participation === 'active' && m.eligibleTypeIds.includes(type.id) && (type.eligibleMerchantIds === 'all' || type.eligibleMerchantIds.includes(m.id)));
  function submitTransfer() {
    const result = transfer(gift.id, transferTo, transferReason);
    if (!result.ok) {
      setTransferError(result.error ?? 'Transfer refused.');
      return;
    }
    setTransferOpen(false);
    setTransferTo('');
    setTransferReason('');
    setTransferError(null);
  }
  return <div className="space-y-6" data-id="element-567" data-mp="11dr28s7pjv51">
      <Panel title={type.name} description={gift.publicReference} action={<Badge tone={statusTone(state)} plain={state === 'available'} data-id="element-569" data-mp="3r3m1q1g9k36b">
            {STATUS_LABEL[state]}
          </Badge>} data-id="element-568" data-mp="11cpg9sp5lo8j">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-570" data-mp="3m62on4vaxwfy">
          <div data-id="element-571" data-mp="21nigns0i05a5">
            <p className="text-body-sm text-ink-muted" data-id="element-572" data-mp="21juksm7kz6s9">Remaining value</p>
            <p className="cz-num mt-1 text-h1 font-semibold text-ink" data-id="element-573" data-mp="26lbik8urqd4m">{koboToNaira(value)}</p>
            <p className="cz-num mt-1 text-body-sm text-ink-muted" data-id="element-574" data-mp="2biad2sicgm3c">
              Face value {koboToNaira(gift.faceValueKobo)}
            </p>
          </div>
          {redeemable && <div className="rounded-card border border-line bg-canvas p-3 text-center" data-id="element-575" data-mp="2l8my0lu2mha0">
              <QRCodeSVG value={`campuzon://campagift/${gift.publicReference}`} size={116} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#101828" data-id="element-576" data-mp="2pai403my5iu4" />
              <p className="mt-2 text-caption text-ink-muted" data-id="element-577" data-mp="zwlfer1poubt">Present for scanning</p>
            </div>}
        </div>

        {gift.message && <blockquote className="mt-4 border-l-[3px] border-brand-700 bg-canvas px-4 py-3 text-body text-ink" data-id="element-578" data-mp="14vyafz2ql7ph">
            “{gift.message}”
            <footer className="mt-1 text-body-sm text-ink-muted" data-id="element-579" data-mp="2qdpctnjb438t">
              from {gift.issuer.name}
            </footer>
          </blockquote>}

        {gift.acceptance === 'pending' && <div className="mt-4 rounded-card border border-warn-100 bg-warn-50 px-4 py-3" data-id="element-580" data-mp="30bwgj4ol4osv">
            <p className="text-body font-semibold text-ink" data-id="element-581" data-mp="2cm25msnttab9">This gift needs your acceptance</p>
            <p className="mt-1 text-body-sm text-ink-muted" data-id="element-582" data-mp="27mxw20wzpake">
              {type.name} is configured to require acceptance before it becomes redeemable.
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-583" data-mp="2lfnha34p2bgg">
              <Button variant="primary" size="sm" icon={<CheckIcon aria-hidden className="h-4 w-4" data-id="element-585" data-mp="1sxjkqn9usj2d" />} onClick={() => accept(gift.id)} data-id="element-584" data-mp="2gndwf1ifs1l1">
                Accept gift
              </Button>
              <Button variant="secondary" size="sm" icon={<XIcon aria-hidden className="h-4 w-4" data-id="element-587" data-mp="1eqgd1szpdh1d" />} onClick={() => decline(gift.id)} data-id="element-586" data-mp="1oun6f8rcj948">
                Decline
              </Button>
            </div>
          </div>}

        {state === 'expired' && <div className="mt-4 flex items-start gap-2 rounded-card border border-warn-100 bg-warn-50 px-4 py-3 text-body-sm text-ink" data-id="element-588" data-mp="19sn7n2684r25">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-warn-700" data-id="element-589" data-mp="1xjsif2xb4t5l" />
            <span data-id="element-590" data-mp="22hc9j3f3t0ey">
              This gift expired on {gift.expiresAt} and can no longer be redeemed.{' '}
              {koboToNaira(value)} remained at expiry. The value and its record are preserved — nothing
              was silently deleted. Treatment of unredeemed value follows the applicable commercial and
              accounting policy.
            </span>
          </div>}

        {gift.frozenReason && <div className="mt-4 flex items-start gap-2 rounded-card border border-danger-100 bg-danger-50 px-4 py-3 text-body-sm text-ink" data-id="element-591" data-mp="h2elz0chboit">
            <ShieldIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-danger-700" data-id="element-592" data-mp="2es3bawbkhrdd" />
            <span data-id="element-593" data-mp="2a13ers6d1bbe">Frozen pending review: {gift.frozenReason}</span>
          </div>}

        <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4" data-id="element-594" data-mp="2531syzhjuxw8">
          <Button variant="secondary" size="sm" icon={<KeyRoundIcon aria-hidden className="h-4 w-4" data-id="element-596" data-mp="201cfld6uu84z" />} onClick={() => revealCode(gift.id)} disabled={!redeemable} data-id="element-595" data-mp="203gtmnygwniz">
            {code ? 'Token shown below' : 'Show redemption code'}
          </Button>
          <Button variant="secondary" size="sm" icon={<ArrowRightLeftIcon aria-hidden className="h-4 w-4" data-id="element-598" data-mp="3lgry89kens7g" />} onClick={() => setTransferOpen(true)} disabled={!redeemable || type.transferability !== 'transferable'} data-id="element-597" data-mp="1m47dtpe6bvfj">
            Transfer
          </Button>
          <Button variant="ghost" size="sm" icon={<FileTextIcon aria-hidden className="h-4 w-4" data-id="element-600" data-mp="3szst66h54dj7" />} onClick={() => setTermsOpen(true)} data-id="element-599" data-mp="3o0g9tvph9old">
            Terms
          </Button>
          <Button variant="ghost" size="sm" icon={<FlagIcon aria-hidden className="h-4 w-4" data-id="element-602" data-mp="6jeyj8ru9uez" />} onClick={() => setDisputeOpen(true)} data-id="element-601" data-mp="1nq41juof3pj">
            Report a problem
          </Button>
        </div>

        {code && <div className="mt-3 rounded-card border border-line bg-canvas px-4 py-3" data-id="element-603" data-mp="3a1yqea6luy99">
            <p className="cz-num text-h4 font-semibold tracking-wide text-ink" data-id="element-604" data-mp="3ezi1j180auwz">{code}</p>
            <p className="mt-1.5 flex items-start gap-2 text-caption text-ink-muted" data-id="element-605" data-mp="3jzwbiot64exn">
              <EyeIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-606" data-mp="uau3l9b02ptx" />
              Give this only to a merchant redeeming your gift. It is a credential: Campuzon stores it
              hashed, never in plain text, and repeated wrong entries are rate limited and flagged.
            </p>
          </div>}
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2" data-id="element-607" data-mp="1ffo1gqoslepy">
        <Panel title="Where this gift can be used" data-id="element-608" data-mp="1u395qnxeljzg">
          <dl className="space-y-3 text-body-sm" data-id="element-609" data-mp="1u4tzwcft30ws">
            <div data-id="element-610" data-mp="1pcssp4xhw0dm">
              <dt className="text-ink-muted" data-id="element-611" data-mp="1pc981gggmxvw">Scope</dt>
              <dd className="mt-0.5 text-ink" data-id="element-612" data-mp="1pag448x8errl">
                {type.eligibleCategoryIds === 'all' ? 'Any category at participating merchants' : `Only ${type.eligibleCategoryIds.join(', ')}`}
              </dd>
            </div>
            <div data-id="element-613" data-mp="2d0sj23qw9oro">
              <dt className="text-ink-muted" data-id="element-614" data-mp="2d1bz1ewzi332">Transferability</dt>
              <dd className="mt-0.5 text-ink" data-id="element-615" data-mp="28422lfb6gr5q">{TRANSFER_LABEL[type.transferability]}</dd>
            </div>
            <div data-id="element-616" data-mp="ip5brhqtfvue">
              <dt className="text-ink-muted" data-id="element-617" data-mp="iom3ksqywltk">Redemption</dt>
              <dd className="mt-0.5 text-ink" data-id="element-618" data-mp="dpj2tki0t1re">
                {type.partialRedemption ? `Partial redemption allowed, from ${koboToNaira(type.minRedemptionKobo)}` : 'Must be redeemed in one full transaction'}
                {type.maxRedemptionPerTransactionKobo ? ` · up to ${koboToNaira(type.maxRedemptionPerTransactionKobo)} per transaction` : ''}
              </dd>
            </div>
            <div data-id="element-619" data-mp="2kjjp6gebm8zb">
              <dt className="text-ink-muted" data-id="element-620" data-mp="26lv3x7gccztq">Funding origin</dt>
              <dd className="mt-0.5 text-ink" data-id="element-621" data-mp="26kjw8wjrz7xc">{FUNDING_LABEL[type.funding]}</dd>
            </div>
            <div data-id="element-622" data-mp="2bm9b6na4c5qp">
              <dt className="text-ink-muted" data-id="element-623" data-mp="2bilg3jp0zb9v">Merchant commission</dt>
              <dd className="mt-0.5 text-ink" data-id="element-624" data-mp="1wp8hie8da0pl">
                {bpsLabel(type.merchantCommissionBps)} deducted from the merchant's receivable, not
                from your value
              </dd>
            </div>
          </dl>

          <p className="mt-4 border-t border-line pt-3 text-body-sm font-medium text-ink" data-id="element-625" data-mp="3qzvoiksvpwwh">
            {eligibleMerchants.length} eligible merchant
            {eligibleMerchants.length === 1 ? '' : 's'}
          </p>
          <ul className="mt-2 flex flex-wrap gap-2" data-id="element-626" data-mp="2rgcte64r9f9v">
            {eligibleMerchants.map(merchant => <li key={merchant.id} className="rounded-md border border-line px-2.5 py-1.5 text-caption text-ink-muted" data-id="element-627" data-mp="2crypdrvlrkrq">
                {merchant.name} · {merchant.category}
              </li>)}
            {eligibleMerchants.length === 0 && <li className="text-caption text-ink-muted" data-id="element-628" data-mp="3ag0ieq2dzncx">
                No eligible merchants are currently accepting this gift type.
              </li>}
          </ul>
        </Panel>

        <Panel title="Instrument history" bodyClassName="p-0" data-id="element-629" data-mp="30grqe2r30it7">
          <ul className="divide-y divide-line" data-id="element-630" data-mp="30j48cfwuat69">
            <li className="px-5 py-3.5" data-id="element-631" data-mp="1k1zz5t5o9hlh">
              <p className="text-body-sm font-medium text-ink" data-id="element-632" data-mp="1k55q7dxv2tju">Issued</p>
              <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-633" data-mp="1p2xii92f4hok">
                {koboToNaira(gift.faceValueKobo)} · {gift.issuedAt} · by {gift.issuer.name}
              </p>
            </li>
            {gift.transfers.map(item => <li key={item.id} className="px-5 py-3.5" data-id="element-634" data-mp="g57c60eq4ii4">
                <p className="text-body-sm font-medium text-ink" data-id="element-635" data-mp="g4nv0lwwdy55">Transferred</p>
                <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-636" data-mp="c4ngl19stmy3">
                  @{item.fromHandle} → @{item.toHandle} · {item.at}
                </p>
              </li>)}
            {history.map(item => <li key={item.id} className="px-5 py-3.5" data-id="element-637" data-mp="3fm5mv49xalwe">
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-638" data-mp="3tbza5i5s4y40">
                  <p className="text-body-sm font-medium text-ink" data-id="element-639" data-mp="3okhbx1v55bmq">
                    {item.reversalOfReference ? 'Reversal' : item.state === 'reversed' ? 'Redemption (reversed)' : 'Redemption'}
                  </p>
                  <span className={`cz-num text-body-sm font-semibold ${item.amountKobo < 0 ? 'text-ok-700' : 'text-ink'}`} data-id="element-640" data-mp="ztyuk82r3023">
                    {item.amountKobo < 0 ? '+' : '−'}
                    {koboToNaira(Math.abs(item.amountKobo)).replace('−', '')}
                  </span>
                </div>
                <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-641" data-mp="guupfaebxqfp">
                  {item.reference} · {item.at.slice(0, 10)} ·{' '}
                  {merchants.find(m => m.id === item.merchantId)?.name}
                </p>
                {item.reason && <p className="mt-1 text-caption text-ink-muted" data-id="element-642" data-mp="quvcn7fbkqp6">Reason: {item.reason}</p>}
                {item.reversalOfReference && <p className="cz-num mt-1 text-caption text-ink-muted" data-id="element-643" data-mp="226w44u3kit8m">
                    Compensating entry for {item.reversalOfReference} — the original record is
                    preserved above.
                  </p>}
              </li>)}
            {history.length === 0 && <li className="px-5 py-4 text-body-sm text-ink-muted" data-id="element-644" data-mp="3mqi2rjrvffh9">
                No redemption history yet.
              </li>}
          </ul>
        </Panel>
      </div>

      <Modal open={transferOpen} onClose={() => setTransferOpen(false)} title="Transfer remaining value" description={`${koboToNaira(value)} would move to the new holder. Redemption history stays with the instrument.`} footer={<>
            <Button variant="secondary" onClick={() => setTransferOpen(false)} data-id="element-646" data-mp="2z16y3r7eh0xu">
              Cancel
            </Button>
            <Button variant="primary" onClick={submitTransfer} data-id="element-647" data-mp="2u2lx3pkbqd8c">
              Transfer gift
            </Button>
          </>} data-id="element-645" data-mp="3cz3xevd8nlg7">
        <div className="space-y-4" data-id="element-648" data-mp="s9o7e2lug0rm">
          <Field label="New holder" htmlFor="transfer-to" hint="Campuzon handle, e.g. @chidi.n" data-id="element-649" data-mp="sapn4kdt1bfo">
            <Input id="transfer-to" value={transferTo} onChange={event => setTransferTo(event.target.value)} data-id="element-650" data-mp="3m2pcp2nq3rx9" />
          </Field>
          <Field label="Reason" htmlFor="transfer-reason" hint="Recorded on the instrument" data-id="element-651" data-mp="3c49igqteg2jj">
            <Input id="transfer-reason" value={transferReason} onChange={event => setTransferReason(event.target.value)} data-id="element-652" data-mp="3h21jqbfxp10e" />
          </Field>
          {transferError && <p className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2 text-body-sm text-danger-700" data-id="element-653" data-mp="3756mrwzb78o8">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-654" data-mp="2tb5muzwmffma" />
              {transferError}
            </p>}
        </div>
      </Modal>

      <Modal open={disputeOpen} onClose={() => {
      setDisputeOpen(false);
      setDisputeSent(false);
    }} title="Report a problem with this gift" footer={disputeSent ? <Button variant="secondary" onClick={() => {
      setDisputeOpen(false);
      setDisputeSent(false);
    }} data-id="element-656" data-mp="24tx1vjx35gv9">
              Close
            </Button> : <>
              <Button variant="secondary" onClick={() => setDisputeOpen(false)} data-id="element-657" data-mp="29plxjc5zidma">
                Cancel
              </Button>
              <Button variant="primary" disabled={!disputeDetail.trim()} onClick={() => {
        raiseDispute(gift.id, disputeKind, disputeDetail);
        setDisputeSent(true);
        setDisputeDetail('');
      }} data-id="element-658" data-mp="2nollehloaohe">
                Raise dispute
              </Button>
            </>} data-id="element-655" data-mp="2jfcdfpe7828q">
        {disputeSent ? <p className="text-body text-ink" data-id="element-659" data-mp="32gnglocsstb3">
            Your dispute is open and queued for a reviewer. You will be notified when a decision is
            recorded. Nothing about the instrument changes while it is under review.
          </p> : <div className="space-y-4" data-id="element-660" data-mp="3efss2e08dfq7">
            <Field label="What went wrong" htmlFor="dispute-kind" data-id="element-661" data-mp="3jgylke1pt70s">
              <select id="dispute-kind" value={disputeKind} onChange={event => setDisputeKind(event.target.value)} className="min-h-touch-lg w-full rounded-lg border border-line-strong bg-surface px-3.5 text-body text-ink focus:border-brand-700 focus:outline-none" data-id="element-662" data-mp="3o7yi3i6x9n2r">
                <option data-id="element-663" data-mp="63n691n2ywpg">Incorrect redemption amount</option>
                <option data-id="element-664" data-mp="b16ki1js39he">Unauthorised redemption</option>
                <option data-id="element-665" data-mp="b0wuwf2nb4c8">Merchant would not accept the gift</option>
                <option data-id="element-666" data-mp="27ui6q6s8fk2i">Expired gift dispute</option>
                <option data-id="element-667" data-mp="27w1lfsy8pn26">Refund not received</option>
              </select>
            </Field>
            <Field label="Detail" htmlFor="dispute-detail" data-id="element-668" data-mp="2cuw3gsodv7l8">
              <Textarea id="dispute-detail" rows={4} value={disputeDetail} onChange={event => setDisputeDetail(event.target.value)} placeholder="What happened, where, and when." data-id="element-669" data-mp="25abyvo0hz9go" />
            </Field>
          </div>}
      </Modal>

      <Modal open={termsOpen} onClose={() => setTermsOpen(false)} title={`${type.name} terms`} footer={<Button variant="secondary" onClick={() => setTermsOpen(false)} data-id="element-671" data-mp="2j8ifzsh88utn">
            Close
          </Button>} data-id="element-670" data-mp="1qi9ubqnhfsab">
        <ul className="space-y-2.5" data-id="element-672" data-mp="2e8nopntji382">
          {type.terms.map(term => <li key={term} className="flex items-start gap-2 text-body text-ink-muted" data-id="element-673" data-mp="29bdu7tran1dd">
              <CheckIcon aria-hidden className="mt-1 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-674" data-mp="12ujtkeb7u91f" />
              {term}
            </li>)}
        </ul>
      </Modal>
    </div>;
}